/**
 * Community Info pop-up — not a registry slot.
 *
 * Rendered by Header (nav trigger + open state live there). Token paths stay
 * under the Header slot (`header.*`); this file is just JSX nesting so Header
 * doesn't own the whole modal markup. contentSchema remains on Header.
 *
 * Modal content: Call Us / Visit Us / Office Hours + Contact + Apply Now CTA.
 * Pet policy lives on `/pets`; fees live on `/apply-now#fees` — not duplicated here.
 */
import { useContentToken } from '@vibesites/runtime/react';
import { hasValue } from '../lib/petPolicyList';

export interface CommunityInfoHoursRow {
  label: string;
  hours: string;
}

export interface CommunityInfoModalProps {
  open: boolean;
  onClose: () => void;
  phone: string | null | undefined;
  formatPhone: (raw: string | null | undefined) => string;
  address: string | null | undefined;
  addressUrl: string | null | undefined;
  addressTarget?: string | null;
  hours: CommunityInfoHoursRow[];
  /** Apply Now CTA URL — the on-site `/apply-now` page. */
  applyNowUrl?: string | null;
  applyNowTarget?: string | null;
}

export function CommunityInfoModal({
  open,
  onClose,
  phone,
  formatPhone,
  address,
  addressUrl,
  addressTarget,
  hours,
  applyNowUrl,
  applyNowTarget,
}: CommunityInfoModalProps) {
  // Always shown for Homestead (default label). VALUE "" hides only if a site
  // explicitly blanks it — otherwise Apply Now is always present.
  const feeGuideCtaLabel = useContentToken('header.fee_guide_cta_label', {
    defaultValue: 'Apply Now',
  });
  const feeGuideHref = (applyNowUrl || '/apply-now').trim() || '/apply-now';
  const showFeeGuide = hasValue(feeGuideCtaLabel);

  return (
    <div
      className={`fixed inset-0 z-[70] flex items-center justify-center p-4 transition-[visibility] duration-300 ${
        open ? 'visible' : 'invisible pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Community Info"
    >
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        className={`community-info-modal relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg shadow-2xl transition-all duration-300 ${
          open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <button
          onClick={onClose}
          className="community-info-close absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center"
          aria-label="Close Community Info"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="overflow-y-auto px-7 py-9 sm:px-10 sm:py-11">
          <h2 className="community-info-title font-heading text-3xl sm:text-4xl text-center mb-9">
            Community Info
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
            <div className="flex flex-col gap-7">
              <div>
                <h3 className="community-info-heading">Call Us</h3>
                <a
                  href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref community-info-value block"
                >
                  {formatPhone(phone) || phone}
                </a>
              </div>

              <div>
                <h3 className="community-info-heading">Visit Us</h3>
                <a
                  href={addressUrl || '#'}
                  target={addressTarget || '_blank'}
                  rel="noopener noreferrer"
                  className="community-info-value block whitespace-pre-line"
                >
                  {address}
                </a>
              </div>
            </div>

            {hours.length > 0 && (
              <div>
                <h3 className="community-info-heading">Office Hours</h3>
                <dl className="community-info-hours">
                  {hours.map((row) => (
                    <div key={row.label} className="community-info-hours-row">
                      <dt>{row.label}</dt>
                      <dd>{row.hours}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>

          <div className={`community-info-cta-row${showFeeGuide ? ' community-info-cta-row--pair' : ''}`}>
            <a href="/contact-us" onClick={onClose} className="community-info-cta">
              Contact Our Team
            </a>
            {showFeeGuide && (
              <a
                href={feeGuideHref}
                target={applyNowTarget || '_self'}
                onClick={onClose}
                className="community-info-cta"
              >
                {String(feeGuideCtaLabel)}
              </a>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .community-info-modal {
          background: var(--vibe-color-background);
          border: 1px solid var(--vibe-color-border);
          color: var(--vibe-color-text);
        }
        .community-info-title {
          font-weight: 400;
          color: var(--vibe-color-text);
        }
        .community-info-close {
          color: var(--vibe-color-text);
          opacity: 0.7;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .community-info-close:hover { opacity: 1; transform: rotate(90deg); }
        .community-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: var(--vibe-color-text);
        }
        .community-info-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        a.community-info-value { color: var(--vibe-color-text); }
        a.community-info-value:hover { text-decoration: underline; }
        .community-info-cta-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 2rem;
        }
        .community-info-cta-row--pair .community-info-cta {
          flex: 1 1 calc(50% - 0.4rem);
          min-width: 10rem;
        }
        .community-info-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          color: #fff;
          background: #000;
          border: 1px solid #000;
          padding: 0.9rem 1.25rem;
          border-radius: 2px;
          text-align: center;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .community-info-cta:hover {
          background: #fff;
          color: #000;
        }
        .community-info-hours {
          display: flex;
          flex-direction: column;
        }
        .community-info-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 0.35rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text-muted);
        }
        .community-info-hours-row dt { color: var(--vibe-color-text); }
      `}</style>
    </div>
  );
}
